import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { TopNavigation } from '@/components/TopNavigation'
import { Toast } from '@/components/Toast'
import { useAuth } from '@/hooks/useAuth'
import { FirmwareService, ChargerFirmwareService, UserProfileHelper } from '@lura/shared'
import type { FirmwareVersion, ChargerFirmwareVersion } from '@lura/shared'

type FirmwareRecord = FirmwareVersion | ChargerFirmwareVersion

interface FirmwareServiceAdapter {
  label: string
  getAll: () => Promise<FirmwareRecord[]>
  create: (
    version: string,
    description: string | null,
    releaseNotes: string[],
    filePath: string | null,
    fileSizeBytes: number | null,
    isReleased: boolean
  ) => Promise<FirmwareRecord>
  update: (
    id: string,
    updates: {
      description?: string | null
      release_notes?: string[]
      is_released?: boolean
    }
  ) => Promise<FirmwareRecord>
  remove: (id: string) => Promise<void>
  uploadFile: (file: File, path: string) => Promise<string>
  deleteFile: (path: string) => Promise<void>
}

const sensorAdapter: FirmwareServiceAdapter = {
  label: 'Sensor',
  getAll: () => FirmwareService.getAllFirmwareVersions(),
  create: (v, d, n, fp, fsb, ir) =>
    FirmwareService.createFirmwareVersion(v, d, n, fp, fsb, ir),
  update: (id, updates) => FirmwareService.updateFirmwareVersion(id, updates),
  remove: (id) => FirmwareService.deleteFirmwareVersion(id),
  uploadFile: (file, path) => FirmwareService.uploadFirmwareFile(file, path),
  deleteFile: (path) => FirmwareService.deleteFirmwareFile(path)
}

const chargerAdapter: FirmwareServiceAdapter = {
  label: 'Charger',
  getAll: () => ChargerFirmwareService.getAllChargerFirmwareVersions(),
  create: (v, d, n, fp, fsb, ir) =>
    ChargerFirmwareService.createChargerFirmwareVersion(v, d, n, fp, fsb, ir),
  update: (id, updates) =>
    ChargerFirmwareService.updateChargerFirmwareVersion(id, updates),
  remove: (id) => ChargerFirmwareService.deleteChargerFirmwareVersion(id),
  uploadFile: (file, path) =>
    ChargerFirmwareService.uploadChargerFirmwareFile(file, path),
  deleteFile: (path) => ChargerFirmwareService.deleteChargerFirmwareFile(path)
}

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface PanelProps {
  adapter: FirmwareServiceAdapter
  onSuccess: (msg: string) => void
  onError: (msg: string) => void
}

const FirmwareTabPanel: React.FC<PanelProps> = ({ adapter, onSuccess, onError }) => {
  const [versions, setVersions] = useState<FirmwareRecord[]>([])
  const [loading, setLoading] = useState(true)

  const [showUploadForm, setShowUploadForm] = useState(false)
  const [formVersion, setFormVersion] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formReleaseNotes, setFormReleaseNotes] = useState('')
  const [formIsReleased, setFormIsReleased] = useState(false)
  const [formFile, setFormFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDescription, setEditDescription] = useState('')
  const [editReleaseNotes, setEditReleaseNotes] = useState('')
  const [editIsReleased, setEditIsReleased] = useState(false)

  const fetchVersions = async () => {
    try {
      setLoading(true)
      const data = await adapter.getAll()
      setVersions(data)
    } catch (err) {
      console.error(`Error fetching ${adapter.label} firmware versions:`, err)
      onError(err instanceof Error ? err.message : 'Failed to load firmware versions')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVersions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter])

  const resetForm = () => {
    setFormVersion('')
    setFormDescription('')
    setFormReleaseNotes('')
    setFormIsReleased(false)
    setFormFile(null)
    setShowUploadForm(false)
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formVersion.trim()) {
      onError('Version number is required')
      return
    }

    try {
      setUploading(true)

      let filePath: string | null = null
      let fileSizeBytes: number | null = null

      if (formFile) {
        const path = `${formVersion.trim()}/${formFile.name}`
        await adapter.uploadFile(formFile, path)
        filePath = path
        fileSizeBytes = formFile.size
      }

      const notes = formReleaseNotes
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)

      await adapter.create(
        formVersion.trim(),
        formDescription.trim() || null,
        notes,
        filePath,
        fileSizeBytes,
        formIsReleased
      )

      resetForm()
      onSuccess(`${adapter.label} firmware version created successfully`)
      await fetchVersions()
    } catch (err) {
      console.error(`Error creating ${adapter.label} firmware version:`, err)
      onError(err instanceof Error ? err.message : 'Failed to create firmware version')
    } finally {
      setUploading(false)
    }
  }

  const handleToggleRelease = async (v: FirmwareRecord) => {
    try {
      await adapter.update(v.id, { is_released: !v.is_released })
      onSuccess(`Version ${v.version} ${v.is_released ? 'unreleased' : 'released'}`)
      await fetchVersions()
    } catch (err) {
      console.error('Error toggling release:', err)
      onError(err instanceof Error ? err.message : 'Failed to update')
    }
  }

  const handleStartEdit = (v: FirmwareRecord) => {
    setEditingId(v.id)
    setEditDescription(v.description || '')
    setEditReleaseNotes((v.release_notes || []).join('\n'))
    setEditIsReleased(v.is_released)
  }

  const handleSaveEdit = async (id: string) => {
    try {
      const notes = editReleaseNotes
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)

      await adapter.update(id, {
        description: editDescription.trim() || null,
        release_notes: notes,
        is_released: editIsReleased
      })

      setEditingId(null)
      onSuccess('Firmware version updated')
      await fetchVersions()
    } catch (err) {
      console.error('Error updating firmware version:', err)
      onError(err instanceof Error ? err.message : 'Failed to update')
    }
  }

  const handleDelete = async (v: FirmwareRecord) => {
    if (!window.confirm(`Delete ${adapter.label.toLowerCase()} firmware version ${v.version}? This cannot be undone.`)) {
      return
    }

    try {
      if (v.file_path) {
        await adapter.deleteFile(v.file_path).catch(() => {})
      }
      await adapter.remove(v.id)
      onSuccess(`Version ${v.version} deleted`)
      await fetchVersions()
    } catch (err) {
      console.error('Error deleting firmware version:', err)
      onError(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  return (
    <div>
      {/* Upload action row inside the tab panel */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {adapter.label} Firmware Versions
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage firmware releases for Lura {adapter.label.toLowerCase()} devices
          </p>
        </div>
        <button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-3xl text-white bg-dark-blue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-blue transition-colors"
        >
          {showUploadForm ? 'Cancel' : `+ Upload New ${adapter.label} Firmware`}
        </button>
      </div>

      {showUploadForm && (
        <div className="mb-6 bg-white shadow-sm rounded-3xl p-6">
          <h3 className="text-xl font-medium text-dark-blue mb-4">
            Upload New {adapter.label} Firmware
          </h3>
          <form onSubmit={handleUpload}>
            <div className="space-y-4">
              <div>
                <label htmlFor={`version-${adapter.label}`} className="block text-sm font-medium text-gray-700 mb-1">
                  Version
                </label>
                <input
                  type="text"
                  id={`version-${adapter.label}`}
                  value={formVersion}
                  onChange={e => setFormVersion(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
                  placeholder="e.g., 1.3.0"
                  disabled={uploading}
                  required
                />
              </div>
              <div>
                <label htmlFor={`description-${adapter.label}`} className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  id={`description-${adapter.label}`}
                  value={formDescription}
                  onChange={e => setFormDescription(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
                  placeholder="Brief description of this release"
                  disabled={uploading}
                />
              </div>
              <div>
                <label htmlFor={`releaseNotes-${adapter.label}`} className="block text-sm font-medium text-gray-700 mb-1">
                  Release Notes (one per line)
                </label>
                <textarea
                  id={`releaseNotes-${adapter.label}`}
                  value={formReleaseNotes}
                  onChange={e => setFormReleaseNotes(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
                  rows={4}
                  placeholder={'Improved battery life\nFixed sensor calibration\nAdded new data mode'}
                  disabled={uploading}
                />
              </div>
              <div>
                <label htmlFor={`firmwareFile-${adapter.label}`} className="block text-sm font-medium text-gray-700 mb-1">
                  Firmware File (.gbl)
                </label>
                <input
                  type="file"
                  id={`firmwareFile-${adapter.label}`}
                  accept=".gbl"
                  onChange={e => setFormFile(e.target.files?.[0] || null)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
                  disabled={uploading}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`isReleased-${adapter.label}`}
                  checked={formIsReleased}
                  onChange={e => setFormIsReleased(e.target.checked)}
                  className="h-4 w-4 text-dark-blue focus:ring-dark-blue border-gray-300 rounded"
                  disabled={uploading}
                />
                <label htmlFor={`isReleased-${adapter.label}`} className="text-sm text-gray-700">
                  Release immediately (visible to all users)
                </label>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-3xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-3xl text-sm font-medium text-white bg-dark-blue hover:bg-opacity-90 transition-colors disabled:opacity-50"
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Firmware'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      )}

      {!loading && (
        <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-highlight-green">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                    Version
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                    File
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                    Created
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-dark-blue uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {versions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                      No {adapter.label.toLowerCase()} firmware versions uploaded yet.
                    </td>
                  </tr>
                ) : (
                  versions.map(v => (
                    <tr key={v.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {v.version}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {editingId === v.id ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={editDescription}
                              onChange={e => setEditDescription(e.target.value)}
                              className="w-full px-3 py-1.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-dark-blue"
                              placeholder="Description"
                            />
                            <textarea
                              value={editReleaseNotes}
                              onChange={e => setEditReleaseNotes(e.target.value)}
                              className="w-full px-3 py-1.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-dark-blue"
                              rows={3}
                              placeholder="Release notes (one per line)"
                            />
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={editIsReleased}
                                onChange={e => setEditIsReleased(e.target.checked)}
                                className="h-3.5 w-3.5 text-dark-blue border-gray-300 rounded"
                              />
                              <span className="text-xs text-gray-600">Released</span>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-gray-900 max-w-xs truncate">
                              {v.description || '—'}
                            </p>
                            {v.release_notes && v.release_notes.length > 0 && (
                              <p className="text-xs text-gray-500 mt-1 max-w-xs truncate">
                                {v.release_notes.length} note{v.release_notes.length !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-500">
                          {v.file_path ? formatFileSize(v.file_size_bytes) : '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          v.is_released
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {v.is_released ? 'Released' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-500">
                          {new Date(v.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {editingId === v.id ? (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleSaveEdit(v.id)}
                              className="text-sm text-green-600 hover:text-green-800 font-medium"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-3">
                            <button
                              onClick={() => handleStartEdit(v)}
                              className="text-sm text-dark-blue hover:text-opacity-80 font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleRelease(v)}
                              className="text-sm text-dark-blue hover:text-opacity-80 font-medium"
                            >
                              {v.is_released ? 'Unrelease' : 'Release'}
                            </button>
                            <button
                              onClick={() => handleDelete(v)}
                              className="text-sm text-red-600 hover:text-red-800 font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

type TabKey = 'sensor' | 'charger'

export const FirmwareManagementPage: React.FC = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabKey>('sensor')

  if (!UserProfileHelper.isWebAdmin(user)) {
    return <Navigate to="/" replace />
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const adapter = activeTab === 'sensor' ? sensorAdapter : chargerAdapter

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Firmware"
        onSignOut={handleSignOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          <div className="mb-8">
            <h2
              className="text-5xl font-normal text-dark-blue"
              style={{ fontFamily: 'Family' }}
            >
              Firmware Management
            </h2>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex gap-6" aria-label="Firmware tabs">
              <button
                onClick={() => setActiveTab('sensor')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'sensor'
                    ? 'border-dark-blue text-dark-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Sensor Firmware
              </button>
              <button
                onClick={() => setActiveTab('charger')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'charger'
                    ? 'border-dark-blue text-dark-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Charger Firmware
              </button>
            </nav>
          </div>

          <FirmwareTabPanel
            key={activeTab}
            adapter={adapter}
            onSuccess={setSuccessMessage}
            onError={setErrorMessage}
          />
        </div>
      </main>

      <Toast
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />
      <Toast
        message={errorMessage}
        type="error"
        onClose={() => setErrorMessage(null)}
      />
    </div>
  )
}
